/* Twilio Inbound Flow editor — DTMF menu + AI intent routing config */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Tree, Plus, Trash, FloppyDisk, Robot, HashStraight } from '@phosphor-icons/react';

const MODES = [
    { v: 'menu_then_ai', l: 'Menu + AI fallback' },
    { v: 'menu_only', l: 'Pouze DTMF menu' },
    { v: 'ai_only', l: 'Pouze AI asistentka' },
    { v: 'direct_ring', l: 'Přímo na klapku' },
];

const ACTIONS = [
    { v: 'ring_extension', l: 'Klapka v CMS' },
    { v: 'ring_number', l: 'Externí číslo (E.164)' },
    { v: 'voicemail', l: 'Hlasová schránka' },
];

const TwilioInboundFlow = () => {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/twilio/settings').then((r) => {
            setCfg(r.data.inbound_flow || {});
        });
    }, []);

    const upd = (patch) => setCfg({ ...cfg, ...patch });

    const updOption = (idx, patch) => {
        const opts = [...(cfg.options || [])];
        opts[idx] = { ...opts[idx], ...patch };
        upd({ options: opts });
    };
    const addOption = () => {
        upd({ options: [...(cfg.options || []),
            { digit: String((cfg.options || []).length + 1), label: 'Volba',
              action: 'ring_extension', target: '' }] });
    };
    const delOption = (idx) => {
        const opts = [...(cfg.options || [])];
        opts.splice(idx, 1);
        upd({ options: opts });
    };

    const updIntent = (idx, patch) => {
        const arr = [...(cfg.ai_intents || [])];
        arr[idx] = { ...arr[idx], ...patch };
        upd({ ai_intents: arr });
    };
    const addIntent = () => {
        upd({ ai_intents: [...(cfg.ai_intents || []),
            { name: 'novy_intent', description: '', target_extension: '' }] });
    };
    const delIntent = (idx) => {
        const arr = [...(cfg.ai_intents || [])];
        arr.splice(idx, 1);
        upd({ ai_intents: arr });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/twilio/settings', { inbound_flow: cfg });
            toast.success('Call flow uložen');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chyba');
        } finally {
            setSaving(false);
        }
    };

    if (!cfg) return <div className="py-10 text-center">Načítám…</div>;

    return (
        <div className="max-w-4xl" data-testid="twilio-flow-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <Tree size={28} style={{ color: 'var(--brand)' }} />
                    Twilio Inbound Call Flow
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Konfigurace co se má stát při příchozím hovoru na vaše Twilio číslo
                </p>
            </div>

            <div className="rounded-xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <label className="text-xs">
                    <div className="mb-2" style={{ color: 'var(--text-secondary)' }}>Režim</div>
                    <select data-testid="flow-mode" value={cfg.mode || 'menu_then_ai'}
                            onChange={(e) => upd({ mode: e.target.value })}
                            className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                            style={{ borderColor: 'var(--border-default)' }}>
                        {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                </label>
            </div>

            {(cfg.mode === 'menu_only' || cfg.mode === 'menu_then_ai') && (
                <div className="rounded-xl border p-6 mb-4"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center gap-2 mb-3">
                        <HashStraight size={18} style={{ color: 'var(--brand)' }} />
                        <h2 className="text-base font-bold">DTMF Menu — uvítání + volby</h2>
                    </div>
                    <label className="text-xs block mb-3">
                        <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                            Uvítací zpráva (Polly.Jakub česky)
                        </div>
                        <textarea data-testid="flow-welcome"
                                  rows={2}
                                  value={cfg.welcome_message || ''}
                                  onChange={(e) => upd({ welcome_message: e.target.value })}
                                  className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                                  style={{ borderColor: 'var(--border-default)' }} />
                    </label>
                    <label className="text-xs block mb-4">
                        <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                            Timeout pro volbu (s)
                        </div>
                        <input type="number" value={cfg.menu_timeout_seconds || 5}
                               onChange={(e) => upd({ menu_timeout_seconds: parseInt(e.target.value, 10) })}
                               className="w-24 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm font-mono"
                               style={{ borderColor: 'var(--border-default)' }} />
                    </label>

                    <div className="text-xs uppercase tracking-wider font-bold mb-2"
                         style={{ color: 'var(--text-secondary)' }}>
                        Volby ({(cfg.options || []).length})
                    </div>
                    <div className="space-y-2 mb-3">
                    {(cfg.options || []).map((o, i) => (
                        <div key={i} className="flex gap-2 items-center" data-testid={`flow-opt-${i}`}>
                            <input value={o.digit} maxLength={1}
                                   onChange={(e) => updOption(i, { digit: e.target.value })}
                                   className="w-12 bg-[var(--bg-app)] border rounded-lg px-2 py-2 text-center font-bold font-mono"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <input value={o.label} placeholder="Štítek"
                                   onChange={(e) => updOption(i, { label: e.target.value })}
                                   className="flex-1 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <select value={o.action}
                                    onChange={(e) => updOption(i, { action: e.target.value })}
                                    className="bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                            </select>
                            <input value={o.target || ''}
                                   placeholder={o.action === 'ring_extension' ? '101' : '+420…'}
                                   onChange={(e) => updOption(i, { target: e.target.value })}
                                   className="w-32 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <button onClick={() => delOption(i)}
                                    className="p-2 hover:text-[var(--danger)]">
                                <Trash size={16} />
                            </button>
                        </div>
                    ))}
                    </div>
                    <button data-testid="flow-add-opt" onClick={addOption}
                            className="px-3 py-1 rounded text-xs border hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)' }}>
                        <Plus size={12} className="inline mr-1" /> Přidat volbu
                    </button>
                </div>
            )}

            {(cfg.mode === 'ai_only' || cfg.mode === 'menu_then_ai') && (
                <div className="rounded-xl border p-6 mb-4"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center gap-2 mb-3">
                        <Robot size={18} style={{ color: 'var(--brand)' }} />
                        <h2 className="text-base font-bold">AI Asistentka — intent routing</h2>
                    </div>
                    <label className="text-xs block mb-4">
                        <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                            AI uvítací výzva (před nahráním řeči)
                        </div>
                        <textarea rows={2}
                                  value={cfg.ai_prompt || ''}
                                  onChange={(e) => upd({ ai_prompt: e.target.value })}
                                  className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                                  style={{ borderColor: 'var(--border-default)' }} />
                    </label>

                    <div className="text-xs uppercase tracking-wider font-bold mb-2"
                         style={{ color: 'var(--text-secondary)' }}>
                        AI Intenty (volaný řekne → GPT klasifikuje → spojíme s klapkou/číslem)
                    </div>
                    <div className="space-y-2 mb-3">
                    {(cfg.ai_intents || []).map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center"
                             data-testid={`flow-intent-${i}`}>
                            <input value={it.name} placeholder="poptavka_fve"
                                   onChange={(e) => updIntent(i, { name: e.target.value })}
                                   className="col-span-3 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <input value={it.description || ''} placeholder="Popis intentu"
                                   onChange={(e) => updIntent(i, { description: e.target.value })}
                                   className="col-span-5 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <input value={it.target_extension || ''} placeholder="Klapka"
                                   onChange={(e) => updIntent(i, { target_extension: e.target.value })}
                                   className="col-span-1 bg-[var(--bg-app)] border rounded-lg px-2 py-2 text-sm font-mono text-center"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <input value={it.target_number || ''} placeholder="+420…"
                                   onChange={(e) => updIntent(i, { target_number: e.target.value })}
                                   className="col-span-2 bg-[var(--bg-app)] border rounded-lg px-3 py-2 text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)' }} />
                            <button onClick={() => delIntent(i)}
                                    className="p-2 hover:text-[var(--danger)]">
                                <Trash size={16} />
                            </button>
                        </div>
                    ))}
                    </div>
                    <button data-testid="flow-add-intent" onClick={addIntent}
                            className="px-3 py-1 rounded text-xs border hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)' }}>
                        <Plus size={12} className="inline mr-1" /> Přidat intent
                    </button>
                </div>
            )}

            {cfg.mode === 'direct_ring' && (
                <div className="rounded-xl border p-6 mb-4"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <label className="text-xs">
                        <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                            Klapka (přímo přepojit hovor)
                        </div>
                        <input value={cfg.direct_ring_target || ''} placeholder="101"
                               onChange={(e) => upd({ direct_ring_target: e.target.value })}
                               className="w-32 bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                               style={{ borderColor: 'var(--border-default)' }} />
                    </label>
                </div>
            )}

            <button data-testid="flow-save-btn" onClick={save} disabled={saving}
                    className="px-6 py-2 rounded-lg font-bold text-sm disabled:opacity-50"
                    style={{ background: 'var(--brand)', color: '#000' }}>
                <FloppyDisk size={14} className="inline mr-2" />
                {saving ? 'Ukládám…' : 'Uložit call flow'}
            </button>
        </div>
    );
};

export default TwilioInboundFlow;
